import { type Contract, contract } from '@btsis/contracts';
import { isCurrency } from '@btsis/core';
import { formatMoney, hasTranslation } from '@btsis/ui';
import { createORPCClient, ORPCError } from '@orpc/client';
import type { ContractRouterClient } from '@orpc/contract';
import { OpenAPILink } from '@orpc/openapi-client/fetch';

const CSRF_COOKIE = 'btsis_csrf';
const CSRF_HEADER = 'x-csrf-token';

function appendHeader(source: Headers, name: string, value: string): Headers {
  const headers = new Headers(source);
  headers.set(name, value);
  return headers;
}

function readCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

const link = new OpenAPILink(contract, {
  url: `${window.location.origin}/api`,
  fetch: (request, init) => {
    // Double-submit: the server set this cookie deliberately readable so we can
    // echo it in a header it can compare. A cross-origin page cannot read it.
    const token = readCsrfToken();
    const withToken = token
      ? new Request(request, { headers: appendHeader(request.headers, CSRF_HEADER, token) })
      : request;
    return globalThis.fetch(withToken, { ...init, credentials: 'include' });
  },
});

export const api: ContractRouterClient<Contract> = createORPCClient(link);

export type ApiErrorShape = {
  code: string;
  message: string;
  /**
   * A narrower code the server put in `details.reason` when the generic status
   * code would not tell the user what to do about it — `CONFLICT` alone cannot
   * distinguish "system role" from "still in use".
   */
  reason?: string;
  details?: Record<string, unknown>;
  requestId?: string;
};

type ErrorEnvelope = {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  requestId?: string;
};

export function toApiError(error: unknown): ApiErrorShape {
  if (error instanceof ORPCError) {
    const data = error.data as ErrorEnvelope | undefined;
    const reason = data?.details?.reason;
    return {
      code: data?.code ?? error.code ?? 'INTERNAL_SERVER_ERROR',
      message: data?.message ?? error.message,
      ...(typeof reason === 'string' ? { reason } : {}),
      ...(data?.details ? { details: data.details } : {}),
      ...(data?.requestId ? { requestId: data.requestId } : {}),
    };
  }
  return { code: 'NETWORK', message: 'Network error' };
}

/**
 * The most specific translation key the server gave us for a failure: a reason
 * nested under its code (`error.PRICING_VIOLATION.AMOUNT_BELOW_MINIMUM`), then
 * the reason on its own, then the code.
 */
export function errorKey(error: unknown): string {
  const apiError = toApiError(error);
  const candidates = apiError.reason
    ? [`error.${apiError.code}.${apiError.reason}`, `error.${apiError.reason}`]
    : [];
  return candidates.find(hasTranslation) ?? `error.${apiError.code}`;
}

/**
 * Interpolation values for an error message. Amounts arrive in minor units
 * beside their currency, so each `…AmountMinor` detail is also offered
 * formatted as `…Amount` — the server never sends formatted money.
 */
export function errorParams(error: unknown): Record<string, string> {
  const details = toApiError(error).details ?? {};
  const currency =
    typeof details.currency === 'string' && isCurrency(details.currency) ? details.currency : null;

  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(details)) {
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    params[key] = String(value);
    if (currency && key.endsWith('AmountMinor') && /^-?\d+$/.test(String(value))) {
      params[key.replace(/Minor$/, '')] = formatMoney(String(value), currency);
    }
  }
  return params;
}
